import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { NotificationActions } from '@next/core-lib/notification';
import { SettingsService, SettingsStorage } from '@next/core-lib/settings';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  exhaustMap,
  finalize,
  map,
  startWith,
  switchMap,
  take,
} from 'rxjs/operators';

import { LayerContextKeys } from 'src/app/shared/constants';

import * as deliveryActions from './delivery.actions';

import { SettingsKeys } from '../../shared/components/page-table-filter/page-table-filter.component';
import { DeliveryLine, DeliveryModuleState } from '../models/delivery.model';
import { DeliveryService } from '../services/delivery.service';

@Injectable()
export class DeliveryEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<DeliveryModuleState>,
    private deliveryService: DeliveryService,
    private settingsService: SettingsService,
  ) {}

  loadData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deliveryActions.loadData),
      concatMap((locationIds) =>
        of(
          deliveryActions.clearDeliveryState(),
          deliveryActions.loadDeliveriesRequest(locationIds),
        ),
      ),
    ),
  );

  loadDeliveries$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(deliveryActions.loadDeliveriesRequest),
      switchMap((data) =>
        this.settingsService
          .getSettings({
            contextName: LayerContextKeys.POULTRYNEXTLAYER,
            storage: SettingsStorage.PlatformSession,
            key: SettingsKeys.dateRange,
          })
          .pipe(
            take(1),
            map((settings) => ({ settings, data })),
          ),
      ),
      exhaustMap(({ settings, data }) => {
        const selectedDateRange = settings?.[0]?.data as { [key: string]: string };
        const startDate = selectedDateRange?.startDate
          ? new Date(selectedDateRange?.startDate).toISOString()
          : null;
        const endDate = selectedDateRange?.endDate
          ? new Date(selectedDateRange.endDate).toISOString()
          : null;
        const locations = 'locationIds' in data && data.locationIds;

        return this.deliveryService.getDeliveries(startDate, endDate, locations).pipe(
          switchMap((deliveryLines: DeliveryLine[]) =>
            of(
              deliveryActions.clearDeliveryState(),
              deliveryActions.loadDeliveriesSuccess({ deliveryLines }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              deliveryActions.loadDeliveriesError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  /* UPSERT EFFECTS */
  upsertDeliveryRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deliveryActions.upsertDeliveryRequest),
      exhaustMap(({ tableLine }) =>
        this.deliveryService.upsertDelivery(tableLine).pipe(
          map(() => deliveryActions.upsertDeliverySuccess({ tableLine })),
          catchError((error: HttpErrorResponse) =>
            of(
              deliveryActions.upsertDeliveryError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
